import { createFileRoute } from "@tanstack/react-router";
import { AdminShell } from "@/components/admin/AdminShell";
import { useAdminAppointments, useConsultationSettings } from "@/hooks/useAdminAppointments";
import { formatISTDateTime } from "@/lib/date/ist";
import { Badge } from "@/components/ui/badge";
import { Loader2, ExternalLink, Pencil, Trash2 } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { AdminDataToolbar } from "@/components/admin/import/AdminDataToolbar";
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export const Route = createFileRoute("/_admin/admin/appointments/")({
  component: AdminAppointmentsCalendarPage,
});

function AdminAppointmentsCalendarPage() {
  return (
    <AdminShell>
      <AdminAppointmentsContent />
    </AdminShell>
  );
}

function AdminAppointmentsContent() {
  const { appointments, isLoading, updateAppointment, cancelAppointment } = useAdminAppointments();
  const { data: settings } = useConsultationSettings();
  const [editingApt, setEditingApt] = useState<any>(null);
  const [deletingApt, setDeletingApt] = useState<any>(null);
  const [editForm, setEditForm] = useState<any>({});

  const handleEdit = (apt: any) => {
    setEditingApt(apt);
    setEditForm({
      consultant_id: apt.consultant_id,
      consultation_type_id: apt.consultation_type_id,
      start_time: apt.start_time.split('.')[0], // Localize for datetime-local
      status: apt.status,
      admin_notes: apt.admin_notes || "",
    });
  };

  const onSaveEdit = async () => {
    try {
      await updateAppointment.mutateAsync({
        id: editingApt.id,
        ...editForm
      });
      toast.success("Appointment updated");
      setEditingApt(null);
    } catch (e) {
      toast.error("Failed to update appointment");
    }
  };

  const onCancelConfirm = async () => {
    try {
      await cancelAppointment.mutateAsync(deletingApt.id);
      toast.success("Appointment cancelled");
      setDeletingApt(null);
    } catch (e) {
      toast.error("Failed to cancel appointment");
    }
  };

  const getStatusVariant = (status: string) => {
    switch (status) {
      case "confirmed": return "outline";
      case "pending": return "secondary";
      case "cancelled": return "destructive";
      case "completed": return "default";
      default: return "outline";
    }
  };
  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Appointments</h1>
          <p className="text-sm text-muted-foreground">Manage all health and nutrition consultations.</p>
        </div>
      </header>

      <AdminDataToolbar
        exportName="appointments"
        moduleId="appointments"
        rows={() => appointments.map(a => ({
          Reference: a.reference,
          Status: a.status,
          Type: (a.consultation_types as any)?.name,
          Consultant: (a.consultants as any)?.name,
          Customer: (a.customer_profiles as any)?.full_name,
          Email: (a.customer_profiles as any)?.email,
          Phone: (a.customer_profiles as any)?.phone,
          Date: a.start_time,
          Amount: a.fee_paise / 100,
          Paid: a.is_paid ? "Yes" : "No",
        }))}
      />

      {isLoading ? (
        <div className="flex h-60 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="rounded-md border border-border bg-card">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b bg-muted/40 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Reference</th>
                  <th className="px-4 py-3">Customer</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Consultant</th>
                  <th className="px-4 py-3">Date & Time</th>
                  <th className="px-4 py-3 text-center">Status</th>
                  <th className="sticky right-0 bg-card px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {appointments.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                      No appointments found.
                    </td>
                  </tr>
                ) : (
                  appointments.map((apt) => (
                    <tr key={apt.id} className="hover:bg-muted/30">
                      <td className="px-4 py-3 font-medium">{apt.reference}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col">
                          <span>{(apt.customer_profiles as any)?.full_name}</span>
                          <span className="text-[10px] text-muted-foreground">{(apt.customer_profiles as any)?.email}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">{(apt.consultation_types as any)?.name}</td>
                      <td className="px-4 py-3">{(apt.consultants as any)?.name}</td>
                      <td className="px-4 py-3 text-xs">{formatISTDateTime(apt.start_time)}</td>
                      <td className="px-4 py-3 text-center">
                        <Badge variant={getStatusVariant(apt.status) as any} className="text-[10px] uppercase">
                          {apt.status}
                        </Badge>
                      </td>
                      <td className="sticky right-0 bg-card/80 backdrop-blur-sm px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Link
                            to="/admin/appointments/$appointmentId"
                            params={{ appointmentId: apt.id }}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-primary hover:bg-primary/10"
                            title="View"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Link>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-foreground"
                            onClick={() => handleEdit(apt)}
                            title="Edit"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive hover:bg-destructive/10"
                            onClick={() => setDeletingApt(apt)}
                            title="Cancel"
                            disabled={apt.status === 'cancelled'}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={!!editingApt} onOpenChange={(open) => !open && setEditingApt(null)}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit Appointment</DialogTitle>
            <DialogDescription>
              Modify appointment details for {editingApt?.reference}
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Consultant</Label>
              <Select 
                value={editForm.consultant_id} 
                onValueChange={(v) => setEditForm((f: any) => ({ ...f, consultant_id: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select consultant" />
                </SelectTrigger>
                <SelectContent>
                  {settings?.consultants.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label>Consultation Type</Label>
              <Select 
                value={editForm.consultation_type_id} 
                onValueChange={(v) => setEditForm((f: any) => ({ ...f, consultation_type_id: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {settings?.types.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label>Date & Time</Label>
              <Input
                type="datetime-local"
                value={editForm.start_time}
                onChange={(e) => setEditForm((f: any) => ({ ...f, start_time: e.target.value }))}
              />
            </div>

            <div className="grid gap-2">
              <Label>Status</Label>
              <Select 
                value={editForm.status} 
                onValueChange={(v) => setEditForm((f: any) => ({ ...f, status: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="no_show">No Show</SelectItem>
                  <SelectItem value="rescheduled">Rescheduled</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label>Admin Notes</Label>
              <Input
                value={editForm.admin_notes}
                onChange={(e) => setEditForm((f: any) => ({ ...f, admin_notes: e.target.value }))}
                placeholder="Internal notes..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditingApt(null)}>Cancel</Button>
            <Button onClick={onSaveEdit} disabled={updateAppointment.isPending}>
              {updateAppointment.isPending ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={!!deletingApt} onOpenChange={(open) => !open && setDeletingApt(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Appointment</DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel appointment {deletingApt?.reference}? 
              This will set the status to cancelled but preserve payment records.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeletingApt(null)}>Keep Appointment</Button>
            <Button variant="destructive" onClick={onCancelConfirm} disabled={cancelAppointment.isPending}>
              {cancelAppointment.isPending ? "Cancelling..." : "Confirm Cancellation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
